import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useTranslation } from "../../i18n";

/**
 * Preset cron expressions with human-readable labels.
 */
const PRESETS = [
  { labelKey: "cron.preset.disabled", value: "" },
  { labelKey: "cron.preset.daily10am", value: "0 10 * * *" },
  { labelKey: "cron.preset.daily9am", value: "0 9 * * *" },
  { labelKey: "cron.preset.weeklyMonday", value: "0 10 * * 1" },
  { labelKey: "cron.preset.monthly1st", value: "0 10 1 * *" },
];

const CRON_PARTS = [
  { key: "minute", labelKey: "cron.minute", min: 0, max: 59 },
  { key: "hour", labelKey: "cron.hour", min: 0, max: 23 },
  { key: "day", labelKey: "cron.day", min: 1, max: 31 },
  { key: "month", labelKey: "cron.month", min: 1, max: 12 },
  { key: "weekday", labelKey: "cron.weekday", min: 0, max: 6 },
];

/**
 * CronPicker — user-friendly cron schedule selector.
 *
 * Shows preset dropdown + manual expression input with preview.
 * Value is always a 5-field cron string (or empty for "manual only").
 */
export default function CronPicker({ value = "", onChange, disabled = false }) {
  const { t } = useTranslation();
  const [customMode, setCustomMode] = useState(
    !isPreset(value) && value !== "",
  );
  const [draft, setDraft] = useState(() => parseParts(value));
  const draftRef = useRef(draft);
  const syncingRef = useRef(false);

  const preview = describeCron(value, t);

  const setPreset = (presetValue) => {
    setCustomMode(false);
    onChange(presetValue);
  };

  const setCustom = (cronStr) => {
    if (isValidCron(cronStr)) {
      onChange(cronStr);
    } else {
      onChange(cronStr);
    }
  };

  const commitDraft = useCallback(
    (partsArr) => {
      const cron = partsArr.join(" ");
      if (isValidCron(cron)) {
        syncingRef.current = true;
        onChange(cron);
      }
    },
    [onChange],
  );

  // Sync draft when value changes externally (raw input, preset, etc.)
  useEffect(() => {
    if (syncingRef.current) {
      syncingRef.current = false;
      return;
    }
    const synced = parseParts(value);
    draftRef.current = synced;
    setDraft(synced);
  }, [value]);

  const enterCustomMode = () => {
    const synced = parseParts(value);
    draftRef.current = synced;
    setDraft(synced);
    setCustomMode(true);
  };

  const updatePart = (index, raw) => {
    const d = [...draftRef.current];
    if (raw === "") {
      d[index] = "*";
    } else {
      const n = parseInt(raw, 10);
      const { min, max } = CRON_PARTS[index];
      if (!isNaN(n) && n >= min && n <= max) {
        d[index] = String(n);
      } else {
        // allow free typing; commit only on blur
        d[index] = raw;
      }
    }
    draftRef.current = d;
    setDraft(d);
  };

  const commitPart = (index) => {
    const d = [...draftRef.current];
    const raw = d[index];
    if (raw === "" || raw === "*") {
      d[index] = "*";
    } else {
      const n = parseInt(raw, 10);
      const { min, max } = CRON_PARTS[index];
      if (!isNaN(n) && n >= min && n <= max) {
        d[index] = String(n);
      } else {
        d[index] = "*";
      }
    }
    draftRef.current = d;
    setDraft(d);
    commitDraft(d);
  };

  return (
    <div className="space-y-2">
      <Label>{t("cron.label")}</Label>

      {/* Preset dropdown */}
      <div className="flex gap-2">
        <div className="flex-1">
          <select
            value={customMode ? "__custom__" : value}
            onChange={(e) => {
              if (e.target.value === "__custom__") {
                enterCustomMode();
              } else {
                setPreset(e.target.value);
              }
            }}
            disabled={disabled}
            className="flex h-10 w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0"
          >
            {PRESETS.map((p) => (
              <option key={p.value || "__empty__"} value={p.value}>
                {t(p.labelKey)}
              </option>
            ))}
            {!isPreset(value) && value !== "" && (
              <option value={value}>{value}</option>
            )}
            <option value="__custom__">{t("cron.preset.custom")}</option>
          </select>
        </div>
      </div>

      {/* Manual mode: part-by-part inputs */}
      {customMode && (
        <div className="space-y-2">
          <div className="grid grid-cols-5 gap-1">
            {CRON_PARTS.map((part, i) => (
              <div key={part.key} className="flex flex-col items-center">
                <Label className="text-xs text-c-gray-400 mb-1">
                  {t(part.labelKey)}
                </Label>
                <Input
                  type="text"
                  value={draft[i] === "*" ? "" : draft[i]}
                  placeholder="*"
                  disabled={disabled}
                  onChange={(e) => updatePart(i, e.target.value)}
                  onBlur={() => commitPart(i)}
                  className="text-center text-xs px-1 h-8"
                  maxLength={4}
                />
              </div>
            ))}
          </div>
          {/* Raw expression */}
          <Input
            value={value}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="* * * * *"
            disabled={disabled}
            className="font-mono text-xs"
          />
        </div>
      )}

      {/* Preview */}
      {value && (
        <p className="text-xs text-c-gray-400">
          {t("cron.preview")}: {preview}
        </p>
      )}
      {!value && (
        <p className="text-xs text-c-gray-400">{t("cron.noSchedule")}</p>
      )}
    </div>
  );
}

/* ── Helpers ── */

const PRESET_VALUES = new Set(PRESETS.map((p) => p.value));

function isPreset(value) {
  return PRESET_VALUES.has(value);
}

function parseParts(cron) {
  const parts = (cron || "").trim().split(/\s+/);
  while (parts.length < 5) parts.push("*");
  return parts.slice(0, 5);
}

function isValidCron(str) {
  if (!str || str.trim() === "") return true;
  const parts = str.trim().split(/\s+/);
  return parts.length === 5;
}

/**
 * Human-readable description of a cron expression.
 */
function describeCron(cron, t) {
  if (!cron || cron.trim() === "") return t("cron.desc.manualOnly");
  const p = parseParts(cron);
  const [min, hour, dom, mon, dow] = p;

  const weekdays = [
    t("cron.weekdayNames.sun"),
    t("cron.weekdayNames.mon"),
    t("cron.weekdayNames.tue"),
    t("cron.weekdayNames.wed"),
    t("cron.weekdayNames.thu"),
    t("cron.weekdayNames.fri"),
    t("cron.weekdayNames.sat"),
  ];

  const time =
    hour !== "*" && min !== "*"
      ? `${hour}:${min.padStart(2, "0")}`
      : min !== "*"
        ? `:${min.padStart(2, "0")}`
        : "";

  // Every N minutes
  if (
    min.includes("/") &&
    hour === "*" &&
    dom === "*" &&
    mon === "*" &&
    dow === "*"
  ) {
    const step = min.split("/")[1];
    return t("cron.desc.everyNMinutes", { n: step });
  }

  // Every N hours
  if (hour.includes("/") && dom === "*" && mon === "*" && dow === "*") {
    const step = hour.split("/")[1];
    return t("cron.desc.everyNHours", { n: step });
  }

  // Daily
  if (dom === "*" && mon === "*" && dow === "*" && time) {
    return t("cron.desc.dailyAt", { time });
  }

  // Specific weekday
  if (dom === "*" && mon === "*" && dow !== "*" && time) {
    const day = weekdays[parseInt(dow, 10)] || dow;
    return t("cron.desc.weeklyAt", { day, time });
  }

  // Specific day of month
  if (mon === "*" && dow === "*" && dom !== "*" && time) {
    return t("cron.desc.monthlyAt", { day: dom, time });
  }

  // Fallback: show raw
  return cron;
}
